import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X, ShoppingCart } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'cart';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (message: string, description?: string) => void;
  error: (message: string, description?: string) => void;
  warning: (message: string, description?: string) => void;
  info: (message: string, description?: string) => void;
  cartAdded: (productName: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const toastIcons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5 text-green-500" />,
  error: <XCircle className="w-5 h-5 text-red-500" />,
  warning: <AlertTriangle className="w-5 h-5 text-amber-500" />,
  info: <Info className="w-5 h-5 text-blue-500" />,
  cart: <ShoppingCart className="w-5 h-5 text-natuvia-cta" />,
};

const toastStyles: Record<ToastType, string> = {
  success: 'border-l-green-500',
  error: 'border-l-red-500',
  warning: 'border-l-amber-500',
  info: 'border-l-blue-500',
  cart: 'border-l-natuvia-cta',
};

const DEFAULT_DURATION = 4000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback(
    (toast: Omit<Toast, 'id'>) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const newToast = { ...toast, id };
      setToasts(prev => [...prev, newToast]);

      const duration = toast.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  const success = useCallback(
    (message: string, description?: string) => addToast({ type: 'success', message, description }),
    [addToast]
  );

  const error = useCallback(
    (message: string, description?: string) => addToast({ type: 'error', message, description }),
    [addToast]
  );

  const warning = useCallback(
    (message: string, description?: string) => addToast({ type: 'warning', message, description }),
    [addToast]
  );

  const info = useCallback(
    (message: string, description?: string) => addToast({ type: 'info', message, description }),
    [addToast]
  );

  const cartAdded = useCallback(
    (productName: string) =>
      addToast({
        type: 'cart',
        message: 'Added to cart',
        description: productName,
        duration: 3000,
      }),
    [addToast]
  );

  return (
    <ToastContext.Provider
      value={{ toasts, addToast, removeToast, success, error, warning, info, cartAdded }}
    >
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};

const ToastContainer: React.FC<{ toasts: Toast[]; removeToast: (id: string) => void }> = ({
  toasts,
  removeToast,
}) => (
  <div
    className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
    role="status"
    aria-live="polite"
    aria-label="Notifications"
  >
    <AnimatePresence mode="popLayout">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </AnimatePresence>
  </div>
);

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, x: 100, scale: 0.95 }}
    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    className={`
      pointer-events-auto bg-white rounded-xl shadow-lg border border-natuvia-text/10
      border-l-4 ${toastStyles[toast.type]} p-4 flex items-start gap-3
    `}
    role="alert"
  >
    <div className="flex-shrink-0 mt-0.5">{toastIcons[toast.type]}</div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-natuvia-text text-sm">{toast.message}</p>
      {toast.description && (
        <p className="text-natuvia-text/60 text-sm mt-0.5 truncate">{toast.description}</p>
      )}
    </div>
    <button
      onClick={onClose}
      className="flex-shrink-0 p-1 rounded-lg hover:bg-natuvia-text/5 transition-colors text-natuvia-text/40 hover:text-natuvia-text"
      aria-label="Dismiss notification"
    >
      <X className="w-4 h-4" />
    </button>
  </motion.div>
);
